import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { useCallback, useEffect, useEffectEvent, useState } from 'react';

import {
  type DataTableFeatures,
  type DataTableRowPresentation,
  DataTableSurface
} from 'design-system';

import type { ReactTable, Row } from '@tanstack/react-table';
import { useMatch, useSearchParams } from 'react-router';

import { captureEvent } from '@/services/analytics';

import type { CatalogItem } from '@/entities/catalog-item';
import { useFactsheetOverlayContainer } from '@/features/factsheet';
import { extractExistingParams } from '../../lib/extractExistingParams';
import ActiveFilterChips from './ActiveFilterChips';
import CatalogTableGrid from './CatalogTableGrid/CatalogTableGrid';
import CatalogToolbar from './CatalogToolbar';
import DisplaySettingsPanel from './DisplaySettings/DisplaySettingsPanel';
import TableFilters from './TableFilters/TableFilters';
import useTableFilters from './TableFilters/useTableFilters';

const CatalogTable = ({
  filterOpen,
  setFilterOpen,
  table,
  rows,
  getRowPresentation,
  onRowActivate,
  isForwardCurves,
  expandedData,
  setExpandedData,
  forwardPeriodsInline,
  setForwardPeriodsInline,
  periodGroups,
  setShowDiscontinued,
  showDiscontinued,
  displaySettingsOpen,
  setDisplaySettingsOpen,
  renderFactsheetButton,
  onViewInOtherCatalog,
  hasCodeFilter
}: {
  filterOpen: boolean;
  setFilterOpen: (filterOpen: boolean) => void;
  table: ReactTable<DataTableFeatures, CatalogItem>;
  rows: Row<DataTableFeatures, CatalogItem>[];
  getRowPresentation: (row: Row<DataTableFeatures, CatalogItem>) => DataTableRowPresentation;
  onRowActivate: (row: Row<DataTableFeatures, CatalogItem>) => void;
  isForwardCurves: boolean;
  expandedData: boolean;
  setExpandedData: (newValue: boolean) => void;
  forwardPeriodsInline: boolean;
  setForwardPeriodsInline: Dispatch<SetStateAction<boolean>>;
  periodGroups: Map<string, CatalogItem[]>;
  showDiscontinued: boolean;
  setShowDiscontinued: Dispatch<SetStateAction<boolean>>;
  displaySettingsOpen: boolean;
  setDisplaySettingsOpen: Dispatch<SetStateAction<boolean>>;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
  onViewInOtherCatalog?: (code: string) => void;
  hasCodeFilter?: boolean;
}) => {
  const isInDashboard = Boolean(useMatch('/dashboard/*'));
  const [searchParams] = useSearchParams();
  const { setContainer } = useFactsheetOverlayContainer();
  const { groups, newDataCount } = useTableFilters(table);

  // "Show all" (beyond the partial period count) is orthogonal to whether a row
  // is expanded at all — that's TanStack's own `row.getIsExpanded()`/
  // `row.toggleExpanded()` (see `dataTableFeatures`'s `rowExpandingFeature`),
  // driven directly by the design-system expand toggle composed in the Title cell.
  const [fullyExpandedCodes, setFullyExpandedCodes] = useState<Set<string>>(new Set());

  const onShowAll = useCallback((code: string) => {
    setFullyExpandedCodes(prev => new Set(prev).add(code));
  }, []);

  // `table` is a fresh reference every render (see useCatalogSorting.ts's note
  // on the same hazard), so it can't be a dependency here — that would re-run
  // this effect on every render, and `resetExpanded` triggering a table state
  // update would then produce a new `table` reference, looping forever.
  const resetExpandedState = useEffectEvent(() => {
    table.resetExpanded(true);
  });

  // Reset expansion when the feature turns off or the tab changes.
  useEffect(() => {
    resetExpandedState();
    // oxlint-disable-next-line react-hooks-js/set-state-in-effect
    setFullyExpandedCodes(new Set());
  }, [forwardPeriodsInline, isForwardCurves]);

  useEffect(() => {
    captureEvent('catalogue_filter', extractExistingParams(searchParams));
  }, [searchParams]);

  return (
    <DataTableSurface.Root>
      <DataTableSurface.Controls>
        <CatalogToolbar
          displaySettingsOpen={displaySettingsOpen}
          filterOpen={filterOpen}
          groups={groups}
          isForwardCurves={isForwardCurves}
          setDisplaySettingsOpen={setDisplaySettingsOpen}
          setFilterOpen={setFilterOpen}
          table={table}
        />
        {displaySettingsOpen ? (
          <DisplaySettingsPanel
            expandedData={expandedData}
            forwardPeriodsInline={forwardPeriodsInline}
            isForwardCurves={isForwardCurves}
            setExpandedData={setExpandedData}
            setForwardPeriodsInline={setForwardPeriodsInline}
            setShowDiscontinued={setShowDiscontinued}
            showDiscontinued={showDiscontinued}
            table={table}
          />
        ) : null}
        <ActiveFilterChips table={table} />
      </DataTableSurface.Controls>
      <DataTableSurface.Body>
        <DataTableSurface.Sidebar open={filterOpen}>
          <TableFilters
            groups={groups}
            newDataCount={newDataCount}
            open={filterOpen}
            table={table}
          />
        </DataTableSurface.Sidebar>
        <DataTableSurface.Content ref={setContainer}>
          <CatalogTableGrid
            fullyExpandedCodes={fullyExpandedCodes}
            getRowPresentation={getRowPresentation}
            isForwardCurves={isForwardCurves}
            isInDashboard={isInDashboard}
            onRowActivate={onRowActivate}
            onShowAll={onShowAll}
            hasCodeFilter={hasCodeFilter}
            onViewInOtherCatalog={onViewInOtherCatalog}
            periodGroups={periodGroups}
            renderFactsheetButton={renderFactsheetButton}
            rows={rows}
            table={table}
          />
        </DataTableSurface.Content>
      </DataTableSurface.Body>
    </DataTableSurface.Root>
  );
};

export default CatalogTable;
